import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ClipboardCheck, Copy, Check, ChevronDown, ChevronUp, Trash2, Loader2,
  ExternalLink, Phone, Mail, Star, KanbanSquare,
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { getAuthToken } from '../../utils/auth';
import {
  MOTORIZED_FIELDS, MOTORIZED_CHOICES, CONDITION_QUESTIONS, CONDITION_ITEMS,
} from '../../data/tradeAppraisalConfig';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  new: 'bg-amber-100 text-amber-800 border-amber-300',
  reviewed: 'bg-blue-100 text-blue-800 border-blue-300',
  appraised: 'bg-emerald-100 text-emerald-800 border-emerald-300',
};
const STATUS_STYLES_DARK = {
  new: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
  reviewed: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
  appraised: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
};

const DetailRow = ({ label, value, isDark }) => {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="flex justify-between gap-3 py-1">
      <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{label}</span>
      <span className={`text-xs font-semibold text-right ${isDark ? 'text-white' : 'text-slate-900'}`}>{String(value)}</span>
    </div>
  );
};

const AppraisalDetail = ({ form, isDark }) => {
  const flaggedConditions = CONDITION_QUESTIONS
    .map((q) => ({ ...q, ans: form.condition?.[q.key] }))
    .filter((q) => q.ans?.answer);
  const ratedItems = CONDITION_ITEMS
    .map((it) => ({ ...it, val: form.items?.[it.key] }))
    .filter((it) => it.val?.rating || it.val?.note);
  const motorizedEntries = [...MOTORIZED_FIELDS, ...MOTORIZED_CHOICES]
    .map((f) => ({ ...f, v: form.motorized?.[f.key] }))
    .filter((f) => f.v);

  const box = `rounded-xl border p-3 ${isDark ? 'bg-slate-900/50 border-slate-700' : 'bg-slate-50 border-slate-200'}`;
  const heading = `text-[10px] font-black uppercase tracking-widest mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3" data-testid="trade-detail">
      <div className={box}>
        <p className={heading}>Trade Unit</p>
        <DetailRow label="Type" value={form.vehicle_type} isDark={isDark} />
        <DetailRow label="Unit" value={`${form.year || ''} ${form.make || ''} ${form.model || ''}`.trim()} isDark={isDark} />
        <DetailRow label="Floorplan" value={form.floorplan} isDark={isDark} />
        <DetailRow label="Slide-outs" value={form.slides} isDark={isDark} />
        <DetailRow label="Clear title" value={form.clear_title} isDark={isDark} />
        <DetailRow label="Salvage title" value={form.salvage_title} isDark={isDark} />
        <DetailRow label="Financed" value={form.financed} isDark={isDark} />
        {form.financed === 'Yes' && (
          <>
            <DetailRow label="Lender" value={form.finance_with} isDark={isDark} />
            <DetailRow label="Payment" value={form.current_payment && `$${form.current_payment}`} isDark={isDark} />
            <DetailRow label="Balance owed" value={form.balance_owed && `$${form.balance_owed}`} isDark={isDark} />
          </>
        )}
        <DetailRow label="Stock # of interest" value={form.stock_interest} isDark={isDark} />
        <DetailRow label="Quote #" value={form.quote_number} isDark={isDark} />
        <DetailRow label="Salesperson" value={form.salesperson} isDark={isDark} />
      </div>

      <div className={box}>
        <p className={heading}>Ratings & Certification</p>
        <DetailRow label="Exterior (1-10)" value={form.exterior_rating} isDark={isDark} />
        <DetailRow label="Interior (1-10)" value={form.interior_rating} isDark={isDark} />
        <DetailRow label="Signature" value={form.certification?.signature} isDark={isDark} />
        <DetailRow label="Initials" value={form.certification?.initials} isDark={isDark} />
        <DetailRow label="Certified" value={form.certification?.ack_cert ? 'Yes' : 'No'} isDark={isDark} />
        <DetailRow label="1-hr inspection ack" value={form.certification?.ack_inspection ? 'Yes' : 'No'} isDark={isDark} />
        <DetailRow
          label="Signed at"
          value={form.certification?.signed_at && new Date(form.certification.signed_at).toLocaleString()}
          isDark={isDark}
        />
        {motorizedEntries.length > 0 && (
          <>
            <p className={`${heading} mt-3`}>Motorized</p>
            {motorizedEntries.map((f) => <DetailRow key={f.key} label={f.label} value={f.v} isDark={isDark} />)}
          </>
        )}
      </div>

      {flaggedConditions.length > 0 && (
        <div className={box}>
          <p className={heading}>Condition Check</p>
          {flaggedConditions.map((q) => (
            <div key={q.key} className="py-1">
              <div className="flex justify-between gap-3">
                <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{q.label}</span>
                <span className={`text-xs font-bold ${q.ans.answer === 'Yes' ? 'text-rose-500' : 'text-emerald-500'}`}>{q.ans.answer}</span>
              </div>
              {q.ans.note && <p className={`text-[11px] italic ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>→ {q.ans.note}</p>}
            </div>
          ))}
        </div>
      )}

      {ratedItems.length > 0 && (
        <div className={box}>
          <p className={heading}>Systems & Equipment</p>
          {ratedItems.map((it) => (
            <div key={it.key} className="py-1">
              <div className="flex justify-between gap-3">
                <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{it.label}</span>
                <span className={`text-xs font-bold ${
                  it.val.rating === 'Poor' ? 'text-rose-500' : it.val.rating === 'Good' ? 'text-amber-500' : it.val.rating === 'N/A' ? 'text-slate-400' : 'text-emerald-500'
                }`}>{it.val.rating || '—'}</span>
              </div>
              {it.val.note && <p className={`text-[11px] italic ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>→ {it.val.note}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const TradesTab = ({ isDark, dealer }) => {
  const [appraisals, setAppraisals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [copied, setCopied] = useState(false);

  const shareUrl = `${window.location.origin}/trade-appraisal${dealer?.slug ? `?dealer=${dealer.slug}` : ''}`;
  const styles = isDark ? STATUS_STYLES_DARK : STATUS_STYLES;

  const fetchAppraisals = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/dealer/trade-appraisals`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      setAppraisals(r.data.appraisals || []);
    } catch {
      toast.error('Failed to load trade appraisals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAppraisals(); }, [fetchAppraisals]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Trade appraisal link copied — text or email it to your customer.');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.info(shareUrl);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.put(`${API}/dealer/trade-appraisals/${id}/status`, { status }, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      setAppraisals((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
    } catch {
      toast.error('Failed to update status');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this trade appraisal? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/dealer/trade-appraisals/${id}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      setAppraisals((prev) => prev.filter((a) => a.id !== id));
      toast.success('Appraisal deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const downloadCertificate = async (a) => {
    try {
      const r = await axios.get(`${API}/dealer/trade-appraisals/${a.id}/certificate.pdf`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Trade Appraisal - ${a.customer_name}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not generate the certificate PDF');
    }
  };

  const [pushingId, setPushingId] = useState(null);
  const [estimatingId, setEstimatingId] = useState(null);
  const getEstimate = async (a, refresh = false) => {
    setEstimatingId(a.id);
    try {
      const r = await axios.post(`${API}/dealer/trade-appraisals/${a.id}/estimate`, { refresh }, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      setAppraisals((prev) => prev.map((x) => (x.id === a.id ? { ...x, value_estimate: r.data.estimate } : x)));
      if (!r.data.cached) toast.success('AI value estimate ready');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Estimate failed — try again.');
    } finally {
      setEstimatingId(null);
    }
  };
  const toPipeline = async (a) => {
    setPushingId(a.id);
    try {
      const r = await axios.post(`${API}/dealer/trade-appraisals/${a.id}/to-pipeline`, {}, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      setAppraisals((prev) => prev.map((x) => (x.id === a.id ? { ...x, lead_id: r.data.lead_id, status: x.status === 'new' ? 'reviewed' : x.status } : x)));
      toast.success(r.data.created
        ? `${a.customer_name} added to your pipeline — check the Pipeline tab.`
        : `Trade attached to ${a.customer_name}'s existing pipeline lead.`);
    } catch {
      toast.error('Failed to add to pipeline');
    } finally {
      setPushingId(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="trades-tab">
      {/* Header + share link */}
      <div className={`rounded-2xl border p-5 sm:p-6 ${isDark ? 'bg-slate-900/60 border-slate-700' : 'bg-white border-slate-200'}`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${isDark ? 'bg-amber-400/15 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>
              <ClipboardCheck size={22} />
            </div>
            <div>
              <h1 className={`text-lg md:text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Trades</h1>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Digital Trade Appraisal Certificates — customers complete them on their own, results land here.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={copyLink} className="bg-[#004E86] hover:bg-[#003d6b] text-white" data-testid="trades-copy-link">
              {copied ? <Check size={15} /> : <Copy size={15} />} Copy Customer Link
            </Button>
            <a href={shareUrl} target="_blank" rel="noreferrer">
              <Button variant="outline" className={isDark ? 'border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white bg-transparent' : ''} data-testid="trades-preview-link"><ExternalLink size={15} /> Preview Form</Button>
            </a>
          </div>
        </div>
        <p className={`mt-3 text-[11px] font-mono px-3 py-2 rounded-lg break-all ${isDark ? 'bg-slate-800 text-slate-300' : 'bg-slate-50 text-slate-600 border border-slate-200'}`}>
          {shareUrl}
        </p>
      </div>

      {/* List */}
      <div className="mt-5">
        {loading ? (
          <div className="flex justify-center py-14"><Loader2 className="animate-spin text-[#004E86]" size={30} /></div>
        ) : appraisals.length === 0 ? (
          <div className={`rounded-2xl border border-dashed p-10 text-center ${isDark ? 'border-slate-700 text-slate-400' : 'border-slate-300 text-slate-500'}`} data-testid="trades-empty">
            <ClipboardCheck size={36} className="mx-auto mb-3 opacity-40" />
            <p className="font-semibold">No trade appraisals yet</p>
            <p className="text-sm mt-1">Copy the customer link above and send it to anyone with a trade-in — their completed certificate will show up here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className={`text-xs font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              {appraisals.length} appraisal{appraisals.length === 1 ? '' : 's'}
            </p>
            {appraisals.map((a) => {
              const isOpen = expanded === a.id;
              return (
                <div
                  key={a.id}
                  className={`rounded-2xl border transition-colors ${isDark ? 'bg-slate-900/60 border-slate-700' : 'bg-white border-slate-200'}`}
                  data-testid={`trade-card-${a.id}`}
                >
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : a.id)}
                    className="w-full flex flex-wrap items-center gap-3 p-4 text-left"
                    data-testid={`trade-card-toggle-${a.id}`}
                  >
                    <div className="flex-1 min-w-[180px]">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{a.customer_name}</p>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${styles[a.status] || styles.new}`}>
                          {a.status}
                        </span>
                      </div>
                      <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{a.unit_summary}</p>
                      <div className={`flex flex-wrap gap-3 mt-1 text-[11px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        {a.phone && <span className="inline-flex items-center gap-1"><Phone size={10} />{a.phone}</span>}
                        {a.email && <span className="inline-flex items-center gap-1"><Mail size={10} />{a.email}</span>}
                        {(a.form?.exterior_rating || a.form?.interior_rating) && (
                          <span className="inline-flex items-center gap-1">
                            <Star size={10} /> Ext {a.form?.exterior_rating || '—'} / Int {a.form?.interior_rating || '—'}
                          </span>
                        )}
                        <span>{new Date(a.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                    {isOpen ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
                  </button>

                  {isOpen && (
                    <div className="px-4 pb-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => toPipeline(a)}
                          disabled={pushingId === a.id}
                          className="bg-[#004E86] hover:bg-[#003d6b] text-white h-8"
                          data-testid={`trade-to-pipeline-${a.id}`}
                        >
                          {pushingId === a.id ? <Loader2 className="animate-spin" size={13} /> : <KanbanSquare size={13} />}
                          {a.lead_id ? 'Update Pipeline Lead' : 'Add to Pipeline'}
                        </Button>
                        {a.lead_id && (
                          <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${isDark ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}>
                            In pipeline
                          </span>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => getEstimate(a, !!a.value_estimate)}
                          disabled={estimatingId === a.id}
                          className={`h-8 ${isDark ? 'border-amber-500/40 text-amber-300 hover:bg-amber-500/10 bg-transparent' : 'border-amber-400 text-amber-700 hover:bg-amber-50'}`}
                          data-testid={`trade-estimate-${a.id}`}
                        >
                          {estimatingId === a.id ? <Loader2 className="animate-spin" size={13} /> : <Star size={13} />}
                          {a.value_estimate ? 'Refresh Estimate' : 'AI Value Estimate'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadCertificate(a)}
                          className={`h-8 ${isDark ? 'border-slate-600 text-slate-200 hover:bg-slate-800 bg-transparent' : ''}`}
                          data-testid={`trade-certificate-${a.id}`}
                        >
                          <ClipboardCheck size={13} /> Certificate PDF
                        </Button>
                        {['new', 'reviewed', 'appraised'].map((s) => (
                          <button
                            key={s}
                            type="button"
                            onClick={() => setStatus(a.id, s)}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide border transition-colors ${
                              a.status === s ? styles[s] : isDark ? 'border-slate-700 text-slate-500 hover:text-slate-300' : 'border-slate-200 text-slate-400 hover:text-slate-600'
                            }`}
                            data-testid={`trade-status-${s}-${a.id}`}
                          >
                            {s}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => remove(a.id)}
                          className="ml-auto p-1.5 rounded-lg text-rose-500 hover:bg-rose-500/10"
                          aria-label="Delete appraisal"
                          data-testid={`trade-delete-${a.id}`}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                      {a.value_estimate && (
                        <div
                          className={`mt-3 rounded-xl border p-3 ${isDark ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-300'}`}
                          data-testid={`trade-estimate-card-${a.id}`}
                        >
                          <div className="flex flex-wrap items-baseline gap-2">
                            <span className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                              AI Trade Value Estimate
                            </span>
                            <span className={`text-lg font-black ${isDark ? 'text-white' : 'text-slate-900'}`}>
                              ${Number(a.value_estimate.low).toLocaleString()} – ${Number(a.value_estimate.high).toLocaleString()}
                            </span>
                            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${isDark ? 'bg-slate-800 text-slate-300' : 'bg-white text-slate-600 border border-slate-200'}`}>
                              {a.value_estimate.confidence} confidence
                            </span>
                          </div>
                          <ul className={`mt-1.5 space-y-0.5 text-xs ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                            {(a.value_estimate.factors || []).map((f, i) => <li key={i}>• {f}</li>)}
                          </ul>
                          <p className={`mt-1.5 text-[10px] italic ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                            Ballpark only — final value set after the 1-hour tech inspection.
                          </p>
                        </div>
                      )}
                      <AppraisalDetail form={a.form || {}} isDark={isDark} />
                      {(a.form?.photos || []).length > 0 && (
                        <div className="mt-3">
                          <p className={`text-[10px] font-black uppercase tracking-widest mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                            Photos ({a.form.photos.length})
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {a.form.photos.map((p) => (
                            <a key={p.id || p.url} href={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} target="_blank" rel="noreferrer">
                                <img
                                  src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`}
                                  alt="Trade"
                                  className="w-20 h-20 object-cover rounded-lg border border-slate-300 hover:opacity-80 transition-opacity"
                                  data-testid={`trade-detail-photo-${p.id}`}
                                />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TradesTab;
